import {
  PMNode,
  Process,
  type SegmentStatement,
  type ProcessStatement,
} from '../types';
import type { Segment } from '../migrations/legacyEdge';
import type { StatementNode as DslStatementNode, Expression as DslExpression } from '../types/dslAst';

export function download(filename: string, content: string, mime: string) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([content], { type: mime }));
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

export function exportSVG(svgEl: SVGSVGElement, procId?: number, processes?: Process[]) {
  const clone = svgEl.cloneNode(true) as SVGSVGElement;
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

  const styleEl = document.createElementNS('http://www.w3.org/2000/svg', 'style');
  styleEl.textContent = `
    .node-circle,.node-diamond,.node-hex,.node-soft,.node-bubble{fill:white;stroke:#bbb;stroke-width:1.5}
    .node-label{font-family:'Caveat',cursive;font-size:12px;fill:#555}
    .node-initials{font-family:'Caveat',cursive;font-size:9px;font-weight:700;fill:#2d7a50}
    .thread-line{fill:none;stroke-linecap:round;opacity:0.85}
    .ring-label{font-family:'Caveat',cursive;font-size:12px}
    .node-del{opacity:0}
    .node-g:hover .node-del{opacity:1}
  `;
  clone.insertBefore(styleEl, clone.firstChild);

  const svgStr  = new XMLSerializer().serializeToString(clone);
  const name    = procId && processes ? (processes.find(p => p.id === procId)?.name ?? 'proceso') : 'lienzo';
  const ts      = new Date().toISOString().slice(0, 10);
  download(`process-mapper_${name}_${ts}.svg`, svgStr, 'image/svg+xml');
}

export function exportPNG(svgEl: SVGSVGElement, procId?: number, processes?: Process[]) {
  const clone = svgEl.cloneNode(true) as SVGSVGElement;
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

  const svgStr  = new XMLSerializer().serializeToString(clone);
  const svgBlob = new Blob([svgStr], { type: 'image/svg+xml' });
  const url     = URL.createObjectURL(svgBlob);
  const img     = new Image();
  const SCALE   = 2;
  const w       = svgEl.getBoundingClientRect().width  * SCALE;
  const h       = svgEl.getBoundingClientRect().height * SCALE;

  img.onload = () => {
    const canvas  = document.createElement('canvas');
    canvas.width  = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#f5f2ec';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    URL.revokeObjectURL(url);
    const name = procId && processes ? (processes.find(p => p.id === procId)?.name ?? 'proceso') : 'lienzo';
    const ts   = new Date().toISOString().slice(0, 10);
    canvas.toBlob(blob => {
      if (!blob) return;
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `process-mapper_${name}_${ts}.png`;
      a.click();
    }, 'image/png');
  };
  img.src = url;
}

function nodeLabel(nodeId: string, nodeById: Map<string, PMNode>) {
  return nodeById.get(nodeId)?.label?.trim() || nodeId;
}

function frictionComment(seg: Segment) {
  if (seg.friction === 'none') return '';
  if (seg.friction === 'low') return ' // friction: low';
  if (seg.friction === 'medium') return ' // friction: medium';
  return ' // friction: high';
}

function waitMetadata(seg: Segment) {
  return ` [freq:${seg.freq} dur:${seg.dur}]`;
}

function toNodeArray(nodes: PMNode[] | Record<string, PMNode>) {
  return Array.isArray(nodes) ? nodes : Object.values(nodes);
}

type OpenBlock = {
  id: string;
  flowType: Segment['flowType'];
};

function formatMeta(meta?: Record<string, unknown>) {
  if (!meta || !Object.keys(meta).length) return '';
  const normalized = Object.entries(meta)
    .filter(([, value]) => value !== undefined)
    .map(([key, value]) => `${key}=${String(value)}`);
  return normalized.length ? ` [${normalized.join(' ')}]` : '';
}


function formatLiteral(value: unknown): string {
  if (value === null) return 'null';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

function formatDslExpression(expr?: DslExpression): string {
  if (!expr) return '';
  if (expr.kind === 'Identifier') return expr.name;
  return formatLiteral(expr.value);
}

function renderProcessStatementLine(statement: ProcessStatement): string[] {
  switch (statement.type) {
    case 'step': {
      const mode = statement.syncMode === 'non-blocking' ? 'ASYNC' : 'SYNC';
      const action = statement.action?.trim() || 'step';
      return [
        `${mode} STEP ${statement.from} -> ${statement.to} : ${action} ${formatMeta(statement.meta)}`,
      ];
    }
    case 'assign':
      return [`ASSIGN ${statement.target} = ${formatLiteral(statement.value)}${formatMeta(statement.meta)}`];
    case 'wait': {
      const until = statement.meta?.until;
      if (typeof until === 'string' && until.trim()) {
        return [`WAIT UNTIL ${until.trim()}${formatMeta(statement.meta)}`];
      }
      return [`WAIT FOR ${statement.duration?.trim() || 'duration'}${formatMeta(statement.meta)}`];
    }
    case 'call': {
      const args = statement.args?.map(formatLiteral).join(', ') ?? '';
      return [`CALL ${statement.callee}${args ? `(${args})` : '()'}${formatMeta(statement.meta)}`];
    }
    case 'trigger': {
      const args = statement.args?.map(formatLiteral).join(', ') ?? '';
      return [`TRIGGER ${statement.event}${args ? `(${args})` : '()'}${formatMeta(statement.meta)}`];
    }
    case 'if':
    case 'while':
    case 'parallel':
    case 'on':
    case 'exception':
      return [];
    default:
      return [];
  }
}

function renderStructuredStatements(statements: ProcessStatement[], depth = 1): string[] {
  const indent = '  ';
  const lines: string[] = [];
  const push = (line: string, level = depth) => lines.push(`${indent.repeat(Math.max(level, 0))}${line}`);

  statements.forEach(statement => {
    const directLine = renderProcessStatementLine(statement);
    if (directLine.length) {
      directLine.forEach(line => push(line));
      return;
    }

    switch (statement.type) {
      case 'if': {
        push(`IF ${statement.type}`);
        lines.push(...renderStructuredStatements(statement.then, depth + 1));
        if (statement.else && statement.else.length) {
          push('ELSE');
          lines.push(...renderStructuredStatements(statement.else, depth + 1));
        }
        push('END IF');
        return;
      }
      case 'while':
        push(`WHILE ${statement.guard}`);
        lines.push(...renderStructuredStatements(statement.body, depth + 1));
        push('END WHILE');
        return;
      case 'parallel':
        push('PARALLEL');
        statement.branches.forEach(branch => {
          lines.push(...renderStructuredStatements(branch, depth + 1));
        });
        push('END PARALLEL');
        return;
      case 'on':
        push(`ON ${statement.event}`);
        lines.push(...renderStructuredStatements(statement.body, depth + 1));
        push('END ON');
        return;
      case 'exception':
        push(`EXCEPTION ${statement.name?.trim() || 'error'}`);
        lines.push(...renderStructuredStatements(statement.body, depth + 1));
        push('END EXCEPTION');
        return;
      default:
        return;
    }
  });

  return lines;
}

function renderDslAstStatements(statements: DslStatementNode[], depth = 1): string[] {
  const indent = '  ';
  const lines: string[] = [];
  const push = (line: string, level = depth) => lines.push(`${indent.repeat(Math.max(level, 0))}${line}`);

  statements.forEach(statement => {
    switch (statement.nodeType) {
      case 'StepStatement': {
        const action = formatDslExpression(statement.label) || 'step';
        push(`SYNC STEP ${statement.from} -> ${statement.to} : ${action}`);
        return;
      }
      case 'AssignStatement':
        push(`ASSIGN ${statement.target} = ${formatDslExpression(statement.value)}`);
        return;
      case 'IfStatement':
        push(`IF ${formatDslExpression(statement.condition)}`);
        lines.push(...renderDslAstStatements(statement.thenBranch, depth + 1));
        if (statement.elseBranch.length) {
          push('ELSE');
          lines.push(...renderDslAstStatements(statement.elseBranch, depth + 1));
        }
        push('END IF');
        return;
      case 'WhileStatement':
        push(`WHILE ${formatDslExpression(statement.condition)}`);
        lines.push(...renderDslAstStatements(statement.body, depth + 1));
        push('END WHILE');
        return;
      case 'ParallelStatement':
        push('PARALLEL');
        statement.branches.forEach(branch => {
          lines.push(...renderDslAstStatements(branch, depth + 1));
        });
        push('END PARALLEL');
        return;
      case 'WaitStatement': {
        const waitExpr = formatDslExpression(statement.duration);
        push(waitExpr ? `WAIT FOR ${waitExpr}` : 'WAIT FOR duration');
        return;
      }
      case 'OnStatement':
        push(`ON ${statement.eventName}`);
        lines.push(...renderDslAstStatements(statement.body, depth + 1));
        push('END ON');
        return;
      case 'ExceptionStatement':
        push(`EXCEPTION ${statement.exceptionName || 'error'}`);
        lines.push(...renderDslAstStatements(statement.body, depth + 1));
        push('END EXCEPTION');
        return;
      case 'CallStatement': {
        const args = statement.args.map(formatDslExpression).join(', ');
        push(`CALL ${statement.callee}${args ? `(${args})` : '()'}`);
        return;
      }
      case 'TriggerStatement': {
        const args = statement.args.map(formatDslExpression).join(', ');
        push(`TRIGGER ${statement.eventName}${args ? `(${args})` : '()'}`);
      }
    }
  });

  return lines;
}

function segmentStatementToLines(statement: SegmentStatement, nodeById: Map<string, PMNode>): string[] {
  switch (statement.type) {
    case 'STEP': {
      const mode = statement.sync === 'non-blocking' ? 'ASYNC' : 'SYNC';
      const from = nodeLabel(statement.from, nodeById);
      const to = nodeLabel(statement.to, nodeById);
      const action = statement.action?.trim() || 'step';
      return [`${mode} STEP ${from} -> ${to} : ${action} ${formatMeta(statement.meta)}`];
    }
    case 'ASSIGN':
      return [`ASSIGN ${statement.variable} = ${statement.expression}`];
    case 'WAIT':
      if (statement.mode === 'UNTIL') {
        return [`WAIT UNTIL ${statement.condition?.trim() || 'condition'}`];
      }
      return [`WAIT FOR ${statement.duration?.trim() || 'duration'}`];
    case 'CALL':
      return [`CALL ${statement.name}${statement.args?.trim() ? `(${statement.args.trim()})` : '()'}`];
    case 'TRIGGER':
      return [`TRIGGER ${statement.name}${statement.args?.trim() ? `(${statement.args.trim()})` : '()'}`];
    case 'ON':
      return [`ON ${statement.event}`, `END ON`];
    case 'EXCEPTION':
      return [`EXCEPTION ${statement.error}`, `END EXCEPTION`];
    default:
      return [];
  }
}

function blockHeader(seg: Segment, nodeById: Map<string, PMNode>) {
  const from = nodeLabel(seg.from, nodeById);
  const to = nodeLabel(seg.to, nodeById);
  const condition = seg.condition?.trim() || 'condición';

  switch (seg.flowType) {
    case 'branch':
      return `IF ${condition}:`;
    case 'loop':
      return `WHILE ${condition}:`;
    case 'fail':
      return `TRY:`;
    case 'parallel':
      return `PARALLEL (${from} | ${to}):`;
    case 'wait':
      return `WAIT UNTIL ${to}:`;
    case 'normal':
    default:
      return `BEGIN BLOCK ${from} -> ${to}:`;
  }
}

function blockCloser(flowType: Segment['flowType']) {
  switch (flowType) {
    case 'branch':
      return 'END IF';
    case 'loop':
      return 'END WHILE';
    case 'fail':
      return 'END TRY';
    case 'parallel':
      return 'END PARALLEL';
    case 'wait':
      return 'END WAIT';
    case 'normal':
    default:
      return 'END BLOCK';
  }
}

function segmentBodyLine(seg: Segment, nodeById: Map<string, PMNode>) {
  const from = nodeLabel(seg.from, nodeById);
  const to = nodeLabel(seg.to, nodeById);
  const action = seg.label?.trim() || 'step';
  const condition = seg.condition?.trim();
  const mode = seg.blocking === 'non-blocking' ? 'ASYNC' : 'SYNC';
  const waitInfo = seg.flowType === 'wait' ? waitMetadata(seg) : '';
  const clause = condition ? ` IF ${condition}` : '';

  return `${mode} STEP ${from} -> ${to}: ${action}${waitInfo}${clause}${frictionComment(seg)}`;
}

export function buildStructuredLines(
  segments: Segment[],
  nodeById: Map<string, PMNode>
): string[] {
  const indent = '  ';
  const lines: string[] = [];
  const stack: OpenBlock[] = [];

  const pushIndented = (line: string, depth = stack.length + 1) => {
    lines.push(`${indent.repeat(Math.max(depth, 0))}${line}`);
  };

  const closeUntil = (targetBlockId?: string) => {
    if (!stack.length) return;

    if (!targetBlockId) {
      const current = stack.pop();
      if (current) pushIndented(blockCloser(current.flowType), stack.length + 1);
      return;
    }

    while (stack.length) {
      const current = stack.pop()!;
      pushIndented(blockCloser(current.flowType), stack.length + 1);
      if (current.id === targetBlockId) break;
    }
  };

  segments.forEach((seg) => {
    pushIndented(segmentBodyLine(seg, nodeById));
  });

  while (stack.length) {
    const current = stack.pop()!;
    pushIndented(blockCloser(current.flowType), stack.length + 1);
  }

  return lines;
}

export function processToPseudocode(
  process: Process,
  nodes: PMNode[] | Record<string, PMNode>,
  segments: Segment[]
): string {
  const nodeById = new Map(toNodeArray(nodes).map(n => [n.id, n]));
  const maybeAstStatements = (process as Process & { ast?: { statements?: DslStatementNode[] } }).ast?.statements;
  const body = process.ir?.statements?.length
    ? renderStructuredStatements(process.ir.statements)
    : maybeAstStatements?.length
      ? renderDslAstStatements(maybeAstStatements)
    : segments.some(seg => seg.statement)
      ? segments.flatMap(seg => seg.statement ? segmentStatementToLines(seg.statement, nodeById).map(line => `  ${line}`) : buildStructuredLines([seg], nodeById))
      : buildStructuredLines(segments, nodeById);
  return [`PROCESS ${process.name.toUpperCase()}`, 'BEGIN', ...body, 'END'].join('\n');
}
